import React from 'react';
import {
  LuTrophy,
  LuFlame,
  LuLightbulb,
  LuTrendingUp,
  LuTrendingDown,
  LuCalendar,
  LuUsers,
  LuBell,
  LuAward,
  LuSparkles,
  LuDollarSign,
  LuAlertTriangle,
  LuWifiOff,
  LuPartyPopper,
  LuX,
} from 'react-icons/lu';

import { AppNotification } from '../../services/notificationsService';
import type { IconKey, NotificationVariant } from './variantPresets';
import {
  bennyAssetFor,
  presetFor,
  variantForType,
  iconForType,
} from './variantPresets';

const ICONS: Record<IconKey, React.ComponentType<{ size?: number; color?: string }>> = {
  trophy: LuTrophy,
  flame: LuFlame,
  lightbulb: LuLightbulb,
  'trending-up': LuTrendingUp,
  'trending-down': LuTrendingDown,
  calendar: LuCalendar,
  users: LuUsers,
  bell: LuBell,
  award: LuAward,
  sparkles: LuSparkles,
  dollar: LuDollarSign,
  'alert-triangle': LuAlertTriangle,
  'wifi-off': LuWifiOff,
  party: LuPartyPopper,
};

interface NotificationCardProps {
  notification: AppNotification;
  onClick?: () => void;
  onDismiss?: () => void;
  /** Set to true when used inside a read-only context (manager viewing a server's feed). */
  readOnly?: boolean;
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  notification: n,
  onClick,
  onDismiss,
  readOnly = false,
}) => {
  const variant = variantForType(n.type, n.payload);
  const preset = presetFor(variant);
  const IconCmp = ICONS[iconForType(n.type, variant)] ?? LuBell;
  const isUnread = !n.read_at;

  const cardStyle: React.CSSProperties = {
    background: preset.bgGradient
      ? `linear-gradient(135deg, ${preset.bgGradient[0]} 0%, ${preset.bgGradient[1]} 100%)`
      : 'white',
    borderColor: isUnread ? `${preset.accentBar ?? '#3B82F6'}55` : '#E5E7EB',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 16,
    position: 'relative',
    padding: '14px 16px',
    cursor: onClick && !readOnly ? 'pointer' : 'default',
    boxShadow: isUnread ? `0 4px 12px ${preset.accentBar ?? '#3B82F6'}14` : undefined,
    transition: 'all 120ms ease',
  };

  return (
    <div style={cardStyle} onClick={onClick}>
      {preset.accentBar && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: preset.accentBar,
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
          }}
        />
      )}
      {preset.confettiAccent && (
        <LuSparkles
          size={16}
          color={preset.iconColor}
          style={{ position: 'absolute', top: 10, right: 28, opacity: 0.55 }}
        />
      )}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {bennyAssetFor(preset.bennyPose) ? (
          <img
            src={bennyAssetFor(preset.bennyPose)!}
            alt=""
            style={{ width: 48, height: 48, objectFit: 'contain', flexShrink: 0 }}
          />
        ) : (
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: preset.iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconCmp size={22} color={preset.iconColor} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: variant === 'celebration' ? 17 : 15,
              color: preset.titleColor,
              paddingRight: isUnread ? 20 : 0,
              marginBottom: 4,
              lineHeight: 1.3,
            }}
          >
            {n.title}
          </div>
          <div style={{ fontSize: 13.5, color: '#4B5563', lineHeight: 1.45, marginBottom: 6 }}>
            {n.body}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>{relativeTime(n.created)}</div>
        </div>
      </div>
      {isUnread && (
        <div
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: preset.accentBar ?? '#3B82F6',
          }}
        />
      )}
      {!readOnly && onDismiss && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          style={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            width: 24,
            height: 24,
            border: 'none',
            background: '#F3F4F6',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6B7280',
          }}
          aria-label="Dismiss"
        >
          <LuX size={14} />
        </button>
      )}
    </div>
  );
};

function relativeTime(iso: string): string {
  const now = new Date();
  const created = new Date(iso);
  const diffMs = now.getTime() - created.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return created.toLocaleDateString();
}

export type { NotificationVariant };
